import React from 'react';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { CustomDialog } from 'components/shared/Dialog';
import { formatCurrency } from 'utils';
import { format } from 'date-fns';

export const SubsidyCertificateDialog = () => {
  const { isOpen, handleClose, data } = useModal(ModalsKeys.SubsidyCertificateDialog);

  if (!data) return null;

  const {
    subsidyRequestId,
    oem,
    customer,
    dealer,
    lender,
    subsidyPercentage,
    dnetPercentage,
    approvedSubsidyAmount,
    financeAmount,
    term,
    equipment,
  } = data;

  const today = format(new Date(), 'MMM d, yyyy');
  const equipmentLabel = [equipment?.year, equipment?.make, equipment?.model].filter(Boolean).join(' ') || 'Equipment';

  return (
    <CustomDialog
      label=""
      isOpen={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      actions={
        <Button variant="contained" onClick={handleClose}>
          Close
        </Button>
      }
    >
      <Stack gap="1.5rem" sx={{ p: '0.5rem' }}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack gap="0.25rem">
            <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
              {oem}
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              Subsidy Payment Request
            </Typography>
            <Typography variant="body2" color="text.secondary">
              OEM Financing Subsidy — Dealer Origination
            </Typography>
          </Stack>
          {/* <Stack alignItems="flex-end" gap="0.25rem">
            <Typography variant="caption" color="text.secondary">
              Generated {today}
            </Typography>
          </Stack> */}
        </Stack>

        {/* Meta bar */}
        <Box sx={{ bgcolor: 'grey.100', borderRadius: 1, px: '1rem', py: '0.5rem' }}>
          <Stack direction="row" gap="1.5rem" flexWrap="wrap">
            <Typography variant="caption">
              <strong>OEM</strong>&nbsp;{oem || '—'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              |
            </Typography>
            <Typography variant="caption">
              <strong>Document ID</strong>&nbsp;{subsidyRequestId}
            </Typography>
          </Stack>
        </Box>

        <Divider />

        {/* Parties */}
        <Stack gap="0.75rem">
          <Typography variant="overline" color="primary" fontWeight={700} sx={{ letterSpacing: '0.1em' }}>
            Parties
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <Stack gap="0.125rem">
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                Customer
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {customer || '—'}
              </Typography>
            </Stack>
            <Stack gap="0.125rem">
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                Dealer
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {dealer || '—'}
              </Typography>
            </Stack>
            <Stack gap="0.125rem">
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                Lender
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {lender || '—'}
              </Typography>
            </Stack>
            <Stack gap="0.125rem">
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                Equipment
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {equipmentLabel}
              </Typography>
              {equipment?.year && (
                <Typography variant="caption" color="text.secondary">
                  Model year {equipment.year}
                </Typography>
              )}
              {equipment?.serialNumberVin && (
                <Typography variant="caption" color="text.secondary">
                  - Serial No. {equipment.serialNumberVin}
                </Typography>
              )}
            </Stack>
          </Box>
        </Stack>

        <Divider />

        {/* Finance Terms */}
        <Stack gap="0.75rem">
          <Typography variant="overline" color="primary" fontWeight={700} sx={{ letterSpacing: '0.1em' }}>
            Finance Terms
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
            <Stack gap="0.125rem">
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                Finance Amount
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {formatCurrency(financeAmount, true)}
              </Typography>
            </Stack>
            <Stack gap="0.125rem">
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                Dealer Net Rate (DNET)
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {dnetPercentage}%
              </Typography>
            </Stack>
            <Stack gap="0.125rem">
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                Term
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {term} months
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {Math.round(term / 12)} years
              </Typography>
            </Stack>
          </Box>
        </Stack>

        <Divider />

        {/* Financing Subsidy */}
        <Stack gap="0.75rem">
          <Typography variant="overline" color="primary" fontWeight={700} sx={{ letterSpacing: '0.1em' }}>
            Financing Subsidy
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'primary.light',
                borderRadius: 1,
                p: '1rem',
                textAlign: 'center',
              }}
            >
              <Typography
                variant="caption"
                color="primary"
                sx={{ textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.08em' }}
              >
                Subsidy Rate
              </Typography>
              <Typography variant="h4" fontWeight={700} color="primary" sx={{ my: '0.25rem' }}>
                {subsidyPercentage}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Of finance amount
              </Typography>
            </Box>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'primary.light',
                borderRadius: 1,
                p: '1rem',
                textAlign: 'center',
              }}
            >
              <Typography
                variant="caption"
                color="primary"
                sx={{ textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.08em' }}
              >
                Subsidy Amount
              </Typography>
              <Typography variant="h4" fontWeight={700} color="primary" sx={{ my: '0.25rem' }}>
                {formatCurrency(approvedSubsidyAmount, true)}
              </Typography>
              {lender && (
                <Typography variant="caption" color="text.secondary">
                  Payable to {lender}
                </Typography>
              )}
            </Box>
          </Box>
        </Stack>
      </Stack>
    </CustomDialog>
  );
};
